import {
  b2bPlanObject,
  B2BPlanType,
  b2cPlanObject,
  B2CPlanType,
  customPlanData,
  PlanCategory,
} from "@/pages/billing/pricing-data";
import {
  IActiveSubscriptionPlan,
  IPendingCancellationRequest,
  IPendingDowngradeRequest,
  IPurchasePlan,
  PaymentInterval,
  SubscriptionStatus,
} from "@/types/subscription.types";

export async function selectPlan(planId: string) {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 1500));

  // This is where you would typically:
  // 1. Create/update a subscription in your database
  // 2. Initialize payment provider setup
  // 3. Update user's plan status

  return { success: true, planId };
}

// Check if a plan type can be downgraded (not the smallest plan in its category)
export const canPlanTypeBeDowngraded = (
  planType: B2BPlanType | B2CPlanType
): boolean => {
  return planType !== B2BPlanType.STARTER && planType !== B2CPlanType.ESSENTIAL;
};

// Check if a billing cycle can be downgraded
export const canBillingCycleBeDowngraded = (
  currentInterval: PaymentInterval
): boolean => {
  return currentInterval !== PaymentInterval.MONTHLY;
};

export type PlanDisplay = {
  planId: string;
  type: B2BPlanType | B2CPlanType;
  name: string;
  quantity: number;
  subscriptionId: string;
  interval: PaymentInterval;
  category: PlanCategory;
  uniqueId: string;
  canDowngradePlanType: boolean;
  canDowngradeCycle: boolean;
  status: SubscriptionStatus;
  pendingRequests: {
    downgrade: IPendingDowngradeRequest | null;
    cancellation: IPendingCancellationRequest | null;
  };
};

export const formatPlansForDisplay = (
  activePlans: IActiveSubscriptionPlan[] | undefined
) => {
  if (!activePlans || activePlans.length === 0) return [];

  return activePlans.flatMap(
    ({ _id: subscriptionId, interval, plans, status }) =>
      plans
        .filter((plan) => !plan.isCustomPlan && plan.planId) // exclude custom plans
        .map((plan) => {
          const planInfo = plan.planId!;
          const quantity = plan.quantity;

          const planId = planInfo._id;
          const type = planInfo.planType;
          const name = planInfo.planName;
          const category = planInfo.category;

          return {
            planId,
            type,
            name,
            quantity,
            subscriptionId,
            interval,
            category,
            uniqueId: `${type}_${interval}`,
            canDowngradePlanType: canPlanTypeBeDowngraded(type),
            canDowngradeCycle: canBillingCycleBeDowngraded(interval),
            status,
            pendingRequests: plan.pendingRequests,
          };
        })
  );
};

export const getPriceForThePlan = ({
  planType,
  planDetails,
  subscription,
}: {
  planType?: keyof typeof b2bPlanObject | keyof typeof b2cPlanObject;
  planDetails: IPurchasePlan;
  subscription: IActiveSubscriptionPlan;
}) => {
  if (planDetails.isCustomPlan) {
    return planDetails.addonPlanId?.price;
  }

  if (!planType) return 0;

  const isB2B = planDetails.planId?.category === PlanCategory.B2B;

  const interval = subscription.interval as
    | PaymentInterval.ANNUAL
    | PaymentInterval.QUARTERLY
    | PaymentInterval.MONTHLY;

  const earlyBirdPrices = isB2B
    ? b2bPlanObject[planType as keyof typeof b2bPlanObject]?.earlyBirdPrice
    : b2cPlanObject[planType as keyof typeof b2cPlanObject]?.earlyBirdPrice;

  return parseFloat(earlyBirdPrices[interval]) || 0;
};

export const getPlanFeatures = ({
  planType,
  planDetails,
}: {
  planType?: keyof typeof b2bPlanObject | keyof typeof b2cPlanObject;
  planDetails: IPurchasePlan;
}) => {
  if (planDetails.isCustomPlan) {
    return customPlanData?.features || [];
  }

  if (!planType) return [];

  const isB2B = planDetails.planId?.category === PlanCategory.B2B;

  if (isB2B) {
    return (
      b2bPlanObject[planType as keyof typeof b2bPlanObject]?.features || []
    );
  } else {
    return (
      b2cPlanObject[planType as keyof typeof b2cPlanObject]?.features || []
    );
  }
};
